import { useEffect, useRef, useCallback } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import * as d3 from 'd3';
import { mapBubbleChartData } from './utils';
import { bloxChartColors } from 'themes/constants';
import { CHART_HEIGHT } from 'components/Dashboard/shared/constants';
import { useElementSize } from '@mantine/hooks';
import { toTitleCase } from 'utility/functions';
import WidgetError from 'components/Dashboard/components/WidgetError';
import { useWidgetContext } from 'components/Dashboard/Widget';

export type TBubbleChartData = {
  label: string;
  value: number;
  color?: string;
  radius?: number;
  r?: number;
};

const BubbleChart = () => {
  const { data, config } = useWidgetContext();
  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { ref, width } = useElementSize();
  const animationsComplete = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);

  // Settings.
  const MIN_RADIUS = width < 600 ? 20 : 50; // Minimum radius in pixels
  const WIDTH = isSmScreen ? width : width - 40; // 40px padding.
  const colorPalette = Object.values(bloxChartColors);
  const animationDuration = 1600;

  // Create tooltip function
  const createTooltip = useCallback(
    (
      svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
      textColor: string,
      tooltipBgColor: string,
    ) => {
      // Add drop shadow filter definition if not already present
      const defs = svg.select('defs');
      if (defs.empty()) {
        svg
          .append('defs')
          .append('filter')
          .attr('id', 'bubble-tooltip-shadow')
          .attr('x', '-20%')
          .attr('y', '-20%')
          .attr('width', '140%')
          .attr('height', '140%').html(`
          <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="#000" flood-opacity="0.25"/>
        `);
      }

      const tooltip = svg
        .append('g')
        .attr('class', 'bubble-tooltip')
        .attr('data-testid', 'bubble-tooltip')
        .style('opacity', 0)
        .style('pointer-events', 'none')
        .style('z-index', '9999');

      // Create background rect
      const background = tooltip
        .append('rect')
        .attr('data-testid', 'bubble-tooltip-background')
        .attr('fill', tooltipBgColor)
        .attr('rx', 4)
        .attr('filter', 'url(#bubble-tooltip-shadow)');

      // Create text elements
      const labelText = tooltip
        .append('text')
        .attr('data-testid', 'bubble-tooltip-text')
        .attr('text-anchor', 'middle')
        .attr('font-weight', 'bold')
        .style('font-size', '12px')
        .style('fill', textColor);

      const valueText = tooltip
        .append('text')
        .attr('data-testid', 'bubble-tooltip-text')
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('fill', textColor);

      return {
        tooltip,
        background,
        labelText,
        valueText,
        show: (event: MouseEvent, d: any) => {
          const mouseX = event.offsetX;
          const mouseY = event.offsetY;
          const isRightBoundary = mouseX > WIDTH - 140;

          // Padding for the tooltip box
          const padX = 10;
          const padY = 8;
          const lineHeight = 16;

          // Update text content
          labelText
            .attr('x', padX)
            .attr('y', padY + 12)
            .text(d.data.label);

          valueText
            .attr('x', padX)
            .attr('y', padY + 12 + lineHeight + 4)
            .text(d.data.value);

          // Measure text dimensions and update background
          const labelBBox = (labelText.node() as SVGTextElement).getBBox();
          const valueBBox = (valueText.node() as SVGTextElement).getBBox();

          const maxTextWidth = Math.max(labelBBox.width, valueBBox.width);
          const totalTextHeight = labelBBox.height + valueBBox.height + 4;

          const tooltipWidth = maxTextWidth + padX * 2;
          const tooltipHeight = totalTextHeight + padY * 2;

          // Center the text horizontally within the tooltip
          const centerX = tooltipWidth / 2;
          labelText.attr('x', centerX);
          valueText.attr('x', centerX);

          background
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', tooltipWidth)
            .attr('height', tooltipHeight)
            .attr(
              'class',
              `bubble-tooltip-bg--${d.data.label.replace(/\s+/g, '-').toLowerCase()}`,
            );

          // Position tooltip
          const offset = 12;
          const finalMouseX = isRightBoundary
            ? mouseX - tooltipWidth - offset
            : mouseX + offset;
          const finalMouseY = mouseY - tooltipHeight / 2;

          tooltip
            .attr('transform', `translate(${finalMouseX},${finalMouseY})`)
            .transition()
            .duration(200)
            .style('opacity', 1);
        },
        hide: () => {
          tooltip.transition().duration(200).style('opacity', 0);
        },
        updatePosition: (event: MouseEvent, d: any) => {
          const mouseX = event.offsetX;
          const mouseY = event.offsetY;
          const isRightBoundary = mouseX > WIDTH - 140;

          // Get current tooltip dimensions
          const tooltipBBox = (tooltip.node() as SVGGElement).getBBox();
          const tooltipWidth = tooltipBBox.width;
          const tooltipHeight = tooltipBBox.height;

          const offset = 12;
          const finalMouseX = isRightBoundary
            ? mouseX - tooltipWidth - offset
            : mouseX + offset;
          const finalMouseY = mouseY - tooltipHeight / 2;

          tooltip.attr('transform', `translate(${finalMouseX},${finalMouseY})`);
        },
      };
    },
    [WIDTH],
  );

  useEffect(() => {
    if (!svgRef.current || !data?.results?.length || width <= 0) return;

    // Styles.
    const isLightMode = theme.palette.mode === 'light';
    const textColor = isLightMode
      ? theme.blox.colors.darkgray
      : theme.blox.colors.offwhite;
    const tooltipBgColor = isLightMode
      ? theme.blox.colors.offwhite
      : theme.blox.colors.darkgray;

    const HEIGHT = isSmScreen ? CHART_HEIGHT : CHART_HEIGHT + 40;
    const scale = width < 600 ? 0.6 : 1.5;

    const mappedData = mapBubbleChartData({
      config,
      data,
    });

    if (!mappedData || !mappedData?.length) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    // Create bubble layout with full width
    const pack = d3
      .pack<{
        label: any;
        children: TBubbleChartData[];
      }>()
      .size([WIDTH, HEIGHT])
      .padding(width < 600 ? 2 : 3); // Tighter padding on mobile

    // Create hierarchy of data.
    const hierarchy = d3
      .hierarchy({ label: 'root', children: mappedData })
      .sum((d: any) => d.value)
      .sort((a, b) => (b.value || 0) - (a.value || 0)); // Sort by value descending

    // Create root node.
    const root = pack(hierarchy);

    // Create SVG container.
    const svg = d3
      .select(svgRef.current)
      .attr('width', WIDTH)
      .attr('height', HEIGHT)
      .attr('viewBox', [0, 0, WIDTH, HEIGHT])
      .attr('style', 'max-width: 100%; height: auto;');

    // Create force simulation.
    const simulation = d3
      .forceSimulation(root.leaves())
      .force('center', width < 600 ? null : d3.forceCenter(WIDTH / 2, HEIGHT / 2))
      .force('charge', d3.forceManyBody().strength(5))
      .force(
        'collide',
        d3
          .forceCollide()
          .radius(
            (d: any) =>
              Math.max(MIN_RADIUS, (d.r || 0) * scale) + (width < 600 ? 0.5 : 2),
          )
          .strength(width < 600 ? 0.98 : 0.9),
      )
      .force(
        'x',
        d3
          .forceX(width < 600 ? WIDTH * 0.25 : WIDTH / 2)
          .strength(width < 600 ? 0.4 : 0.2),
      )
      .force('y', d3.forceY(HEIGHT / 2).strength(width < 600 ? 0.02 : 0.1));

    // Create bubble nodes.
    const node = svg
      .append('g')
      .selectAll('g')
      .data(root.leaves())
      .join('g')
      .attr('transform', (d) => `translate(${d.x},${d.y})`)
      .attr('data-testid', 'bubble-chart-node');

    // Create circles.
    node
      .append('circle')
      .attr('data-testid', 'bubble-chart-circle')
      .attr('r', (d) => Math.max(MIN_RADIUS, d.r * scale))
      .attr('fill-opacity', 0.7)
      .attr('fill', (_, i) => colorPalette[i % colorPalette.length])
      .attr('transform', (d) => {
        // Animation: Start from random position on the border.
        const angle = Math.random() * 2 * Math.PI;
        const startX = Math.cos(angle) * Math.max(WIDTH, CHART_HEIGHT);
        const startY = Math.sin(angle) * Math.max(WIDTH, CHART_HEIGHT);
        return `translate(${startX},${startY})`;
      })
      .transition()
      .duration(animationDuration)
      .ease(d3.easeBounceOut) // Add bounce effect.
      .attr('r', (d) => Math.max(MIN_RADIUS, d.r * scale)) // Animate to final radius.
      .attr('transform', 'translate(0,0)') // Move to final position.
      .on('end', () => {
        // Set animation complete after the longer of circle and text animations
        setTimeout(() => {
          animationsComplete.current = true;
        }, 400); // Text fade duration
      });

    // Update positions on each tick--this is for a more horizontal layout.
    simulation.on('tick', () => {
      if (!animationsComplete.current) return;

      node.attr('transform', (d) => {
        const x = Math.max(d.r * scale, Math.min(WIDTH - d.r * scale, d.x));
        const y = Math.max(d.r * scale, Math.min(HEIGHT - d.r * scale, d.y));
        return `translate(${x},${y})`;
      });
    });

    // Labels: Add text labels.
    node
      .append('text')
      .attr('dy', '.3em')
      .style('text-anchor', 'middle')
      .style('font-size', (d) =>
        width < 600 ? '13px' : `${Math.min((d.r * scale) / 2, 16)}px`,
      ) // Fixed size on mobile
      .style('fill', textColor)
      .text((d: any) => {
        const label = toTitleCase(d.data.label) || 'Unknown';
        // On mobile, if bubble is minimum size, truncate to 4 chars
        if (width < 600 && d.r * scale <= MIN_RADIUS) {
          return label.length > 4 ? `${label.slice(0, 4)}...` : label;
        }
        return label.length > 10 ? `${label.slice(0, 10)}...` : label;
      })
      .style('opacity', 0) // Start with invisible text.
      .transition() // Add transition for text.
      .delay(animationDuration) // Wait for circle animation to complete.
      .duration(400) // Fade in duration.
      .style('fill', textColor) // Set text color.
      .style('opacity', 1); // Fade in the text.

    // Create tooltip after all other elements to ensure it appears on top
    const tooltipController = createTooltip(svg, textColor, tooltipBgColor);

    // Add hover effects
    node
      .on('mouseover', function (event, d) {
        if (!animationsComplete.current) return;

        // Hover effect: expand circle and show tooltip.
        d3.select(this)
          .select('circle')
          .transition()
          .duration(300)
          .attr('fill-opacity', 1)
          .attr(
            'r',
            (d: any) => Math.max(MIN_RADIUS, d.r * scale) * (width < 600 ? 1.05 : 1.1),
          ); // Smaller expansion on mobile

        d3.select(this).select('circle').style('opacity', 1);

        tooltipController.show(event, d);
      })
      .on('mousemove', function (event, d) {
        if (!animationsComplete.current) return;
        tooltipController.updatePosition(event, d);
      })
      .on('mouseout', function (event, d) {
        if (!animationsComplete.current) return;

        d3.select(this)
          .select('circle')
          .transition()
          .duration(300)
          .attr('fill-opacity', 0.7)
          .attr('r', (d: any) => Math.max(MIN_RADIUS, d.r * scale)); // Return to original size

        d3.select(this).select('circle').style('opacity', 0.7);
        tooltipController.hide();
      });

    // Stop simulation after 2 seconds
    setTimeout(() => simulation.stop(), 2000);

    // Cleanup
    return () => {
      simulation.stop();
      animationsComplete.current = false;
    };
  }, [
    data,
    WIDTH,
    config,
    colorPalette,
    theme,
    isSmScreen,
    width,
    createTooltip,
    MIN_RADIUS,
  ]);

  return (
    <Box
      ref={ref}
      data-testid="bubble-chart"
      sx={(theme) => {
        const isLightMode = theme.palette.mode === 'light';
        const textColor = isLightMode
          ? theme.blox.colors.darkgray
          : theme.blox.colors.offwhite;
        const backgroundColor = isLightMode
          ? theme.blox.colors.offwhite
          : theme.blox.colors.darkgray;

        return {
          minWidth: {
            xs: 378,
            md: 450,
          },
          width: '100%',
          margin: {
            xs: 0,
            sm: 'auto',
          },
          'bubble-tooltip': {
            backgroundColor,
            borderRadius: 2,
            color: textColor,
          },
        };
      }}
    >
      {/* TODO: error handling */}
      {!data?.results?.length ? (
        <WidgetError type="noData" />
      ) : (
        <svg
          data-testid="bubble-chart-svg"
          ref={svgRef}
        />
      )}
    </Box>
  );
};

export default BubbleChart;
